import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Database,
  Layers,
  ChevronDown,
  ChevronRight,
  Code,
  Rocket,
  Target,
  Users,
  TrendingUp,
  BookOpen
} from 'lucide-react';
import { masterRegistryEntries, registrySections } from '../data/masterRegistryEntries';
import { getFullEncoding } from '../data/fullyEncodedEntries';
import roleParticipationMatrix from '../data/roleBasedParticipationMatrix';
import roleTypesData from '../data/roleTypesData';
import relevanceFrameworkData from '../data/relevanceFrameworkData';
// Lab-only experimental features temporarily removed from M2 page
import { ContextualBreadcrumbs } from '../components/ContextualBreadcrumbs';

import { Navigate } from 'react-router-dom';
import { resolveEntryId, isRedirect } from '../data/entryRedirects';

export const RegistryEntry: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['ontology', 'modality', 'dynamics', 'evaluation'])
  );

  // Check if this is a redirect (old entry ID)
  if (id && isRedirect(id)) {
    const newId = resolveEntryId(id);
    return <Navigate to={`/elevation/entry/${newId}`} replace />;
  }
  
  const entry = masterRegistryEntries.find(e => e.encodedId === id);
  
  // If this is an enhanced framework, redirect to the enhanced page
  if (entry?.enhancedFramework) {
    return <Navigate to={`/elevation/enhanced/${id}`} replace />;
  }
  
  const fullEncoding = entry ? getFullEncoding(entry.encodedId) : undefined;

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  if (!entry) {
    return (
      <div className="text-center py-12">
        <Database className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-charcoal-900 mb-2">Entry Not Found</h2>
        <p className="text-charcoal-600 mb-6">
          The registry entry you're looking for doesn't exist or has been moved.
        </p>
        <Link to="/elevation/models" className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700">
          Back to Models
        </Link>
      </div>
    );
  }

  const section = registrySections.find(s => s.id === entry.sectionCode);

  return (
    <div className="space-y-6">
      {/* Contextual Breadcrumbs */}
      <ContextualBreadcrumbs />
      
      {/* Header */}
      <div>
        <Link to="/elevation/models" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Models
        </Link>
        
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-charcoal-900 mb-2">
                {entry.canonicalName}
              </h1>
              <div className="flex items-center gap-2 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${section?.color || 'gray'}-100 text-${section?.color || 'gray'}-800`}>
                  {entry.sectionCode.toUpperCase()}
                </span>
                <span className="font-mono text-sm text-charcoal-600">{entry.encodedId}</span>
                {entry.shortCode && (
                  <span className="px-2 py-1 bg-[var(--hover-tint)] text-charcoal-700 rounded text-xs font-semibold">
                    {entry.shortCode}
                  </span>
                )}
              </div>
            </div>
          </div>

          {entry.elements && (
            <div className="bg-[var(--surface)] p-4 mb-4">
              <h3 className="font-semibold text-charcoal-900 mb-2">Elements:</h3>
              <p className="text-charcoal-700 whitespace-pre-line">{entry.elements}</p>
            </div>
          )}

          {entry.section && (
            <div className="text-sm text-charcoal-600">
              <span className="font-semibold">Section:</span> {entry.section}
            </div>
          )}
        </div>
      </div>

      {/* Team exploration moved to Lab: see /lab/team-exploration */}
      
      {/* Experimental features moved to Lab during iteration.
          Visit: /lab/smart-suggestions, /lab/relationship-network, /lab/connection-pathfinder, /my-notes, /lab/whats-next */}

      {/* Relevance Framework Detailed Content (if available) */}
      {entry.encodedId === relevanceFrameworkData.encodedId && (
        <div className="space-y-6">
          {/* Core Questions */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">The Three Questions</h2>
              <p className="text-sm text-charcoal-600 mt-1">Sequential questions that create coherence between concept, purpose, and practice</p>
            </div>
            <div className="p-6 space-y-6">
              {relevanceFrameworkData.coreQuestions.map((q, idx) => (
                <div key={idx} className="border-l-4 border-consulting-slate-blue-500 pl-6 py-2">
                  <h3 className="text-lg font-bold text-charcoal-900 mb-2">{idx + 1}. {q.question}</h3>
                  <p className="text-consulting-slate-blue-700 font-medium mb-2">{q.purpose}</p>
                  <p className="text-charcoal-700 mb-2">{q.description}</p>
                  <div className="flex items-start gap-2 mt-3">
                    <span className="text-xs font-semibold text-charcoal-600 uppercase">Outcome:</span>
                    <p className="text-sm text-charcoal-600">{q.outcome}</p>
                  </div>
                  <div className="flex items-start gap-2 mt-2 bg-coral-50 p-2 rounded">
                    <span className="text-xs font-semibold text-coral-700 uppercase">Anti-Pattern:</span>
                    <p className="text-sm text-coral-700">{q.antiPattern}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Purpose */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
            <h2 className="text-xl font-bold text-charcoal-900 mb-4">Purpose</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-consulting-slate-blue-50 p-4 border border-consulting-slate-blue-200">
                <h3 className="font-bold text-consulting-slate-blue-900 mb-2">Clarity</h3>
                <p className="text-sm text-consulting-slate-blue-800">{relevanceFrameworkData.purpose.clarity}</p>
              </div>
              <div className="bg-[var(--mustard-dim)] p-4 border border-[var(--mustard)]">
                <h3 className="font-bold text-[var(--mustard)] mb-2">Significance</h3>
                <p className="text-sm text-[var(--mustard)]">{relevanceFrameworkData.purpose.significance}</p>
              </div>
              <div className="bg-sage-50 p-4 border border-sage-200">
                <h3 className="font-bold text-sage-900 mb-2">Application</h3>
                <p className="text-sm text-sage-800">{relevanceFrameworkData.purpose.application}</p>
              </div>
            </div>
          </div>

          {/* Anti-Patterns */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
            <h2 className="text-xl font-bold text-charcoal-900 mb-4">Common Anti-Patterns to Avoid</h2>
            <div className="space-y-4">
              {relevanceFrameworkData.antiPatterns.map((ap, idx) => (
                <div key={idx} className="bg-coral-50 p-4 border-2 border-coral-200">
                  <h3 className="font-bold text-coral-900 mb-2">{ap.name}</h3>
                  <p className="text-sm text-coral-800 mb-2">{ap.description}</p>
                  <div className="text-sm">
                    <span className="font-semibold text-coral-900">Consequence: </span>
                    <span className="text-coral-700">{ap.consequence}</span>
                  </div>
                  <div className="text-sm mt-2 italic">
                    <span className="font-semibold text-coral-900">Example: </span>
                    <span className="text-coral-700">{ap.example}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Application Levels */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
            <h2 className="text-xl font-bold text-charcoal-900 mb-4">Application Across Levels</h2>
            <div className="space-y-3">
              {relevanceFrameworkData.applicationLevels.map((level, idx) => (
                <div key={idx} className="border-l-4 border-sage-500 pl-4 py-2">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-charcoal-900">{level.level}</h3>
                    <span className="px-2 py-1 bg-sage-100 text-sage-800 rounded text-xs font-medium">
                      {level.timeframe}
                    </span>
                  </div>
                  <p className="text-sm text-charcoal-700 mb-2">{level.context}</p>
                  <p className="text-sm text-sage-700 italic">{level.example}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Role Types Table (if available) */}
      {entry.encodedId === roleTypesData.encodedId && (
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
          <div className="bg-gradient-to-r from-sage-50 to-emerald-50 px-6 py-4 border-b border-[var(--rule)]">
            <h2 className="text-xl font-bold text-charcoal-900">Role Types</h2>
            <p className="text-sm text-charcoal-600 mt-1">{roleTypesData.description}</p>
          </div>

          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--surface)]">
                    <th className="border-2 border-[var(--rule)] px-4 py-3 text-left font-bold text-charcoal-900 w-1/4">
                      Role Type
                    </th>
                    <th className="border-2 border-[var(--rule)] px-4 py-3 text-left font-bold text-charcoal-900">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roleTypesData.roles.map((role, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]'}>
                      <td className="border-2 border-[var(--rule)] px-4 py-3 font-semibold text-charcoal-900 bg-sage-50">
                        {role.roleType}
                      </td>
                      <td className="border-2 border-[var(--rule)] px-4 py-3 text-sm text-charcoal-700">
                        {role.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Matrix Content (if available) */}
      {entry.encodedId === roleParticipationMatrix.encodedId && (
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
          <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b border-[var(--rule)]">
            <h2 className="text-xl font-bold text-charcoal-900">Matrix View</h2>
            <p className="text-sm text-charcoal-600 mt-1">{roleParticipationMatrix.description}</p>
          </div>

          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--surface)]">
                    <th className="border-2 border-[var(--rule)] px-4 py-3 text-left font-bold text-charcoal-900">
                      Role Type
                    </th>
                    <th className="border-2 border-[var(--rule)] px-4 py-3 text-left font-bold text-charcoal-900">
                      Individual-Level Prioritization
                    </th>
                    <th className="border-2 border-[var(--rule)] px-4 py-3 text-left font-bold text-charcoal-900">
                      Team-Level Prioritization
                    </th>
                    <th className="border-2 border-[var(--rule)] px-4 py-3 text-left font-bold text-charcoal-900">
                      Org-Level Prioritization
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roleParticipationMatrix.matrix.map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]'}>
                      <td className="border-2 border-[var(--rule)] px-4 py-3 font-semibold text-charcoal-900 bg-consulting-slate-blue-50">
                        {row.roleType}
                      </td>
                      <td className="border-2 border-[var(--rule)] px-4 py-3 text-sm text-charcoal-700">
                        {row.scopes.individual.participation}
                      </td>
                      <td className="border-2 border-[var(--rule)] px-4 py-3 text-sm text-charcoal-700">
                        {row.scopes.team.participation}
                      </td>
                      <td className="border-2 border-[var(--rule)] px-4 py-3 text-sm text-charcoal-700">
                        {row.scopes.organizational.participation}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Key Insights */}
            {roleParticipationMatrix.insights && (
              <div className="mt-6 bg-sage-50 p-4 border-2 border-sage-200">
                <h3 className="font-bold text-sage-900 mb-3">Key Insights</h3>
                <ul className="space-y-2">
                  {roleParticipationMatrix.insights.map((insight, idx) => (
                    <li key={idx} className="text-sm text-sage-800 flex items-start">
                      <span className="text-sage-600 mr-2">•</span>
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* STARS Model Detail Page */}
      {entry.encodedId === 'pf.framework.diagnostic.context.modes' && (
        <div className="space-y-6">
          {/* Bounded Attributes Showcase */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-[var(--rule)]">
              <div className="flex items-center gap-3">
                <Target className="h-6 w-6 text-[var(--mustard)]" />
                <div>
                  <h2 className="text-xl font-bold text-charcoal-900">Bounded Attributes in Action</h2>
                  <p className="text-sm text-charcoal-600">How STARS connects to the broader system</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Primary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-[var(--mustard-dim)] text-[var(--mustard)] px-2 py-1 rounded text-xs font-bold">PRIMARY</span>
                  Contextual Modes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { mode: 'Starting New', description: 'Launching new initiatives, products, or ventures', color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' },
                    { mode: 'Necessary Ending', description: 'Managing decline, sunsetting, or transitions', color: 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]' },
                    { mode: 'Turnaround', description: 'Rescuing struggling operations or teams', color: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]' },
                    { mode: 'Scaling', description: 'Growing successful operations rapidly', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                    { mode: 'Sustaining', description: 'Maintaining and optimizing current performance', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' }
                  ].map((item, idx) => (
                    <div key={idx} className={`p-4  border-2 ${item.color}`}>
                      <h4 className="font-semibold mb-2">{item.mode}</h4>
                      <p className="text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-sage-100 text-sage-800 px-2 py-1 rounded text-xs font-bold">CONNECTORS</span>
                  Related Attributes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-semibold text-sage-900 mb-2">Scope Level</h4>
                    <p className="text-sm text-sage-700 mb-2">Individual, Team, Organization</p>
                    <p className="text-xs text-sage-600">STARS applies differently at each scope</p>
                  </div>
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-semibold text-consulting-slate-blue-900 mb-2">Role Type</h4>
                    <p className="text-sm text-consulting-slate-blue-700 mb-2">Individual Contributor, Team Lead, Executive</p>
                    <p className="text-xs text-consulting-slate-blue-600">Different roles experience STARS differently</p>
                  </div>
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-semibold text-[var(--mustard)] mb-2">DEGAP Phase</h4>
                    <p className="text-sm text-[var(--mustard)] mb-2">Discover, Evaluate, Generate, Arrange, Prioritize</p>
                    <p className="text-xs text-[var(--mustard)]">STARS affects prioritization approach</p>
                  </div>
                </div>
              </div>

              {/* Practical Usage */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 border-2 border-[var(--mustard)]">
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">How This Works in Practice</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--mustard)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Team Lead in "Starting New" Context</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Team-level | <strong>Role:</strong> Team Lead | <strong>Context:</strong> Starting New
                    </p>
                    <p className="text-sm text-charcoal-600">
                      This combination suggests focusing on rapid prototyping, stakeholder alignment, and building momentum through early wins.
                    </p>
                  </div>
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--mustard)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Executive in "Turnaround" Context</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Organization-level | <strong>Role:</strong> Executive | <strong>Context:</strong> Turnaround
                    </p>
                    <p className="text-sm text-charcoal-600">
                      This combination suggests focusing on crisis management, stakeholder communication, and decisive action to stabilize operations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* STARS Framework Deep Dive */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">STARS Framework Deep Dive</h2>
              <p className="text-sm text-charcoal-600">Understanding situational leadership contexts</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Framework Overview */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Framework Purpose</h3>
                    <p className="text-sm text-charcoal-700 mb-4">
                      STARS helps leaders identify their organizational context and adapt their leadership approach accordingly. 
                      Each context requires different strategies, priorities, and communication styles.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Key Applications</h3>
                    <ul className="space-y-2 text-sm text-charcoal-700">
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Leadership transitions and onboarding</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Strategic planning and resource allocation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Team development and capability building</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Change management and organizational transformation</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Column - Context Matrix */}
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Context Characteristics</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        context: 'Starting New', 
                        characteristics: ['High uncertainty', 'Resource constraints', 'Need for rapid learning'],
                        priority: 'Build momentum'
                      },
                      { 
                        context: 'Necessary Ending', 
                        characteristics: ['Declining performance', 'Resistance to change', 'Emotional complexity'],
                        priority: 'Manage transition'
                      },
                      { 
                        context: 'Turnaround', 
                        characteristics: ['Crisis situation', 'Urgent action needed', 'Stakeholder pressure'],
                        priority: 'Stabilize operations'
                      },
                      { 
                        context: 'Scaling', 
                        characteristics: ['Growth opportunities', 'Resource abundance', 'Process optimization'],
                        priority: 'Sustain growth'
                      },
                      { 
                        context: 'Sustaining', 
                        characteristics: ['Stable performance', 'Incremental improvement', 'Risk management'],
                        priority: 'Optimize efficiency'
                      }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-[var(--surface)] p-3 border border-[var(--rule)]">
                        <h4 className="font-semibold text-charcoal-900 mb-1">{item.context}</h4>
                        <p className="text-xs text-charcoal-600 mb-2">{item.characteristics.join(' • ')}</p>
                        <p className="text-xs font-medium text-consulting-slate-blue-700">Priority: {item.priority}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Frameworks */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-sage-50 to-emerald-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Related Frameworks</h2>
              <p className="text-sm text-charcoal-600">Frameworks that work well with STARS</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-sage-50 border-2 border-sage-200 p-4">
                  <h3 className="font-semibold text-sage-900 mb-2">DEGAP Framework</h3>
                  <p className="text-sm text-sage-700 mb-2">Discover, Evaluate, Generate, Arrange, Prioritize</p>
                  <p className="text-xs text-sage-600">STARS context affects which DEGAP phase to emphasize</p>
                </div>
                <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                  <h3 className="font-semibold text-consulting-slate-blue-900 mb-2">Role Types</h3>
                  <p className="text-sm text-consulting-slate-blue-700 mb-2">Individual Contributor, Team Lead, Executive</p>
                  <p className="text-xs text-consulting-slate-blue-600">Different roles experience STARS contexts differently</p>
                </div>
                <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                  <h3 className="font-semibold text-[var(--mustard)] mb-2">Scope Levels</h3>
                  <p className="text-sm text-[var(--mustard)] mb-2">Individual, Team, Organization</p>
                  <p className="text-xs text-[var(--mustard)]">STARS applies across all organizational levels</p>
                </div>
                <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-4">
                  <h3 className="font-semibold text-[var(--text-sec)] mb-2">Six Kinds of Items</h3>
                  <p className="text-sm text-[var(--text-sec)] mb-2">People, Places, Things, Activities, Information, Time</p>
                  <p className="text-xs text-[var(--text-sec)]">STARS context influences which kinds of items get prioritized</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Four Lifecycle Stages Detail Page */}
      {entry.encodedId === 'pa.lifecycle.stages' && (
        <div className="space-y-6">
          {/* Bounded Attributes Showcase */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-[var(--rule)]">
              <div className="flex items-center gap-3">
                <Target className="h-6 w-6 text-[var(--text-ter)]" />
                <div>
                  <h2 className="text-xl font-bold text-charcoal-900">Bounded Attributes in Action</h2>
                  <p className="text-sm text-charcoal-600">How Four Lifecycle Stages connects to the broader system</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Primary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-[var(--slate-dim)] text-[var(--text-sec)] px-2 py-1 rounded text-xs font-bold">PRIMARY</span>
                  Lifecycle Phases
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { phase: 'Intake', description: 'Capturing and classifying incoming work', color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' },
                    { phase: 'Item Work', description: 'Refining and structuring requirements', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                    { phase: 'Ranking', description: 'Prioritizing and sequencing work', color: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]' },
                    { phase: 'Execution', description: 'Delivering and completing work', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' }
                  ].map((item, idx) => (
                    <div key={idx} className={`p-4  border-2 ${item.color}`}>
                      <h4 className="font-semibold mb-2">{item.phase}</h4>
                      <p className="text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-sage-100 text-sage-800 px-2 py-1 rounded text-xs font-bold">CONNECTORS</span>
                  Related Attributes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-semibold text-sage-900 mb-2">Scope Level</h4>
                    <p className="text-sm text-sage-700 mb-2">Individual, Team, Organization</p>
                    <p className="text-xs text-sage-600">Lifecycle applies differently at each scope</p>
                  </div>
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-semibold text-consulting-slate-blue-900 mb-2">STARS Mode</h4>
                    <p className="text-sm text-consulting-slate-blue-700 mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                    <p className="text-xs text-consulting-slate-blue-600">Context affects lifecycle approach</p>
                  </div>
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-semibold text-[var(--mustard)] mb-2">Resolution Level</h4>
                    <p className="text-sm text-[var(--mustard)] mb-2">Phrase, Sentence, Paragraph, Page</p>
                    <p className="text-xs text-[var(--mustard)]">Granularity affects lifecycle documentation</p>
                  </div>
                </div>
              </div>

              {/* Practical Usage */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-2 border-[var(--slate)]">
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">How This Works in Practice</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--slate)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Team-level "Starting New" Project</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Team-level | <strong>Context:</strong> Starting New | <strong>Phase:</strong> Intake → Item Work
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Focus on rapid intake processes and lightweight item work to build momentum quickly.
                    </p>
                  </div>
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--slate)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Organization-level "Sustaining" Operations</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Organization-level | <strong>Context:</strong> Sustaining | <strong>Phase:</strong> Ranking → Execution
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Emphasize robust ranking processes and efficient execution to maintain performance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lifecycle Framework Deep Dive */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Four Lifecycle Stages Deep Dive</h2>
              <p className="text-sm text-charcoal-600">Understanding work progression through organizational systems</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Framework Overview */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Framework Purpose</h3>
                    <p className="text-sm text-charcoal-700 mb-4">
                      The Four Lifecycle Stages provide a universal framework for understanding how work progresses 
                      through any organizational system, from initial capture to final delivery.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Key Applications</h3>
                    <ul className="space-y-2 text-sm text-charcoal-700">
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Workflow design and optimization</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Process mapping and analysis</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Capacity planning and resource allocation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Quality gates and checkpoints</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Column - Phase Characteristics */}
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Phase Characteristics</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        phase: 'Intake', 
                        characteristics: ['High volume', 'Low fidelity', 'Rapid classification'],
                        focus: 'Capture everything'
                      },
                      { 
                        phase: 'Item Work', 
                        characteristics: ['Medium volume', 'Increasing fidelity', 'Requirement refinement'],
                        focus: 'Structure requirements'
                      },
                      { 
                        phase: 'Ranking', 
                        characteristics: ['Lower volume', 'High fidelity', 'Priority decisions'],
                        focus: 'Sequence work'
                      },
                      { 
                        phase: 'Execution', 
                        characteristics: ['Controlled volume', 'Maximum fidelity', 'Delivery focus'],
                        focus: 'Complete work'
                      }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-[var(--surface)] p-3 border border-[var(--rule)]">
                        <h4 className="font-semibold text-charcoal-900 mb-1">{item.phase}</h4>
                        <p className="text-xs text-charcoal-600 mb-2">{item.characteristics.join(' • ')}</p>
                        <p className="text-xs font-medium text-consulting-slate-blue-700">Focus: {item.focus}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Frameworks */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-sage-50 to-emerald-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Related Frameworks</h2>
              <p className="text-sm text-charcoal-600">Frameworks that work well with Four Lifecycle Stages</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-sage-50 border-2 border-sage-200 p-4">
                  <h3 className="font-semibold text-sage-900 mb-2">DEGAP Framework</h3>
                  <p className="text-sm text-sage-700 mb-2">Decide, Engage, Gather, Arrange, Prioritize</p>
                  <p className="text-xs text-sage-600">Lifecycle stages map to DEGAP phases for prioritization</p>
                </div>
                <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                  <h3 className="font-semibold text-consulting-slate-blue-900 mb-2">Strategy-Delivery Cascade</h3>
                  <p className="text-sm text-consulting-slate-blue-700 mb-2">Strategy, Prioritization, Planning, Delivery</p>
                  <p className="text-xs text-consulting-slate-blue-600">Higher-level strategic phases that contain lifecycle stages</p>
                </div>
                <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                  <h3 className="font-semibold text-[var(--mustard)] mb-2">STARS Context</h3>
                  <p className="text-sm text-[var(--mustard)] mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                  <p className="text-xs text-[var(--mustard)]">Context affects which lifecycle stages to emphasize</p>
                </div>
                <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-4">
                  <h3 className="font-semibold text-[var(--text-sec)] mb-2">Scope Levels</h3>
                  <p className="text-sm text-[var(--text-sec)] mb-2">Individual, Team, Organization</p>
                  <p className="text-xs text-[var(--text-sec)]">Lifecycle applies across all organizational levels</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Strategy-Delivery Cascade Detail Page */}
      {entry.encodedId === 'pf.strategy_delivery.phases' && (
        <div className="space-y-6">
          {/* Bounded Attributes Showcase */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-[var(--rule)]">
              <div className="flex items-center gap-3">
                <Target className="h-6 w-6 text-[var(--text-ter)]" />
                <div>
                  <h2 className="text-xl font-bold text-charcoal-900">Bounded Attributes in Action</h2>
                  <p className="text-sm text-charcoal-600">How Strategy-Delivery Cascade connects to the broader system</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Primary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-[var(--slate-dim)] text-[var(--text-sec)] px-2 py-1 rounded text-xs font-bold">PRIMARY</span>
                  Strategy-Delivery Phases
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { phase: 'Strategy', description: 'Setting direction and defining success', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                    { phase: 'Prioritization', description: 'Ranking and sequencing initiatives', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                    { phase: 'Planning', description: 'Designing execution approach', color: 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]' },
                    { phase: 'Delivery', description: 'Executing and delivering results', color: 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]' }
                  ].map((item, idx) => (
                    <div key={idx} className={`p-4  border-2 ${item.color}`}>
                      <h4 className="font-semibold mb-2">{item.phase}</h4>
                      <p className="text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-sage-100 text-sage-800 px-2 py-1 rounded text-xs font-bold">CONNECTORS</span>
                  Related Attributes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-semibold text-sage-900 mb-2">Scope Level</h4>
                    <p className="text-sm text-sage-700 mb-2">Individual, Team, Organization</p>
                    <p className="text-xs text-sage-600">Strategy-Delivery applies differently at each scope</p>
                  </div>
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-semibold text-consulting-slate-blue-900 mb-2">STARS Mode</h4>
                    <p className="text-sm text-consulting-slate-blue-700 mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                    <p className="text-xs text-consulting-slate-blue-600">Context affects which phases to emphasize</p>
                  </div>
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-semibold text-[var(--mustard)] mb-2">Resolution Level</h4>
                    <p className="text-sm text-[var(--mustard)] mb-2">Phrase, Sentence, Paragraph, Page</p>
                    <p className="text-xs text-[var(--mustard)]">Granularity affects cascade documentation</p>
                  </div>
                </div>
              </div>

              {/* Practical Usage */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 border-2 border-[var(--slate)]">
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">How This Works in Practice</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--slate)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Organization-level "Scaling" Initiative</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Organization-level | <strong>Context:</strong> Scaling | <strong>Phase:</strong> Strategy → Prioritization
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Focus on strategic alignment and robust prioritization to support rapid growth.
                    </p>
                  </div>
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--slate)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Team-level "Turnaround" Project</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Team-level | <strong>Context:</strong> Turnaround | <strong>Phase:</strong> Planning → Delivery
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Emphasize detailed planning and rapid delivery to stabilize operations quickly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Strategy-Delivery Framework Deep Dive */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Strategy-Delivery Cascade Deep Dive</h2>
              <p className="text-sm text-charcoal-600">Understanding strategic execution flow</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Framework Overview */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Framework Purpose</h3>
                    <p className="text-sm text-charcoal-700 mb-4">
                      The Strategy-Delivery Cascade provides a structured approach to translating strategic intent 
                      into executable plans and measurable outcomes across all organizational levels.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Key Applications</h3>
                    <ul className="space-y-2 text-sm text-charcoal-700">
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Strategic planning and execution</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Initiative prioritization and sequencing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Resource allocation and capacity planning</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Cross-functional alignment and coordination</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Column - Phase Characteristics */}
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Phase Characteristics</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        phase: 'Strategy', 
                        characteristics: ['High-level direction', 'Long-term thinking', 'Stakeholder alignment'],
                        focus: 'Define success'
                      },
                      { 
                        phase: 'Prioritization', 
                        characteristics: ['Resource constraints', 'Trade-off decisions', 'Sequencing logic'],
                        focus: 'Rank initiatives'
                      },
                      { 
                        phase: 'Planning', 
                        characteristics: ['Detailed approach', 'Resource allocation', 'Timeline definition'],
                        focus: 'Design execution'
                      },
                      { 
                        phase: 'Delivery', 
                        characteristics: ['Execution focus', 'Progress tracking', 'Outcome measurement'],
                        focus: 'Achieve results'
                      }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-[var(--surface)] p-3 border border-[var(--rule)]">
                        <h4 className="font-semibold text-charcoal-900 mb-1">{item.phase}</h4>
                        <p className="text-xs text-charcoal-600 mb-2">{item.characteristics.join(' • ')}</p>
                        <p className="text-xs font-medium text-consulting-slate-blue-700">Focus: {item.focus}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Frameworks */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-sage-50 to-emerald-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Related Frameworks</h2>
              <p className="text-sm text-charcoal-600">Frameworks that work well with Strategy-Delivery Cascade</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-sage-50 border-2 border-sage-200 p-4">
                  <h3 className="font-semibold text-sage-900 mb-2">DEGAP Framework</h3>
                  <p className="text-sm text-sage-700 mb-2">Decide, Engage, Gather, Arrange, Prioritize</p>
                  <p className="text-xs text-sage-600">DEGAP phases map to Strategy-Delivery phases</p>
                </div>
                <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                  <h3 className="font-semibold text-consulting-slate-blue-900 mb-2">Four Lifecycle Stages</h3>
                  <p className="text-sm text-consulting-slate-blue-700 mb-2">Intake, Item Work, Ranking, Execution</p>
                  <p className="text-xs text-consulting-slate-blue-600">Lifecycle stages operate within Strategy-Delivery phases</p>
                </div>
                <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                  <h3 className="font-semibold text-[var(--mustard)] mb-2">STARS Context</h3>
                  <p className="text-sm text-[var(--mustard)] mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                  <p className="text-xs text-[var(--mustard)]">Context affects which cascade phases to emphasize</p>
                </div>
                <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-4">
                  <h3 className="font-semibold text-[var(--text-sec)] mb-2">Scope Levels</h3>
                  <p className="text-sm text-[var(--text-sec)] mb-2">Individual, Team, Organization</p>
                  <p className="text-xs text-[var(--text-sec)]">Cascade applies across all organizational levels</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role × Scope Matrix Detail Page */}
      {entry.encodedId === 'pf.framework.diagnostic.role_scope.matrix' && (
        <div className="space-y-6">
          {/* Bounded Attributes Showcase */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-[var(--rule)]">
              <div className="flex items-center gap-3">
                <Target className="h-6 w-6 text-[var(--green)]" />
                <div>
                  <h2 className="text-xl font-bold text-charcoal-900">Bounded Attributes in Action</h2>
                  <p className="text-sm text-charcoal-600">How Role × Scope Matrix connects to the broader system</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Primary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] px-2 py-1 rounded text-xs font-bold">PRIMARY</span>
                  Scope Levels
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { scope: 'Individual', description: 'Personal responsibility and accountability', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                    { scope: 'Team', description: 'Group coordination and collaboration', color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' },
                    { scope: 'Organization', description: 'System-wide strategy and governance', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' }
                  ].map((item, idx) => (
                    <div key={idx} className={`p-4  border-2 ${item.color}`}>
                      <h4 className="font-semibold mb-2">{item.scope}</h4>
                      <p className="text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-sage-100 text-sage-800 px-2 py-1 rounded text-xs font-bold">CONNECTORS</span>
                  Related Attributes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-semibold text-sage-900 mb-2">Phase</h4>
                    <p className="text-sm text-sage-700 mb-2">Strategy, Prioritization, Planning, Delivery, Decide, Engage, Gather, Arrange, Prioritize, Intake, Item Work, Ranking, Execution</p>
                    <p className="text-xs text-sage-600">Different phases require different role-scope combinations</p>
                  </div>
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-semibold text-consulting-slate-blue-900 mb-2">STARS Mode</h4>
                    <p className="text-sm text-consulting-slate-blue-700 mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                    <p className="text-xs text-consulting-slate-blue-600">Context affects optimal role-scope alignment</p>
                  </div>
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-semibold text-[var(--mustard)] mb-2">Resolution Level</h4>
                    <p className="text-sm text-[var(--mustard)] mb-2">Phrase, Sentence, Paragraph, Page</p>
                    <p className="text-xs text-[var(--mustard)]">Granularity affects matrix documentation</p>
                  </div>
                </div>
              </div>

              {/* Practical Usage */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 border-2 border-[var(--green)]">
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">How This Works in Practice</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--green)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Team-level "Starting New" Project</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Team-level | <strong>Context:</strong> Starting New | <strong>Phase:</strong> Strategy → Prioritization
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Focus on team coordination and rapid decision-making to establish momentum.
                    </p>
                  </div>
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--green)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Organization-level "Sustaining" Operations</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Organization-level | <strong>Context:</strong> Sustaining | <strong>Phase:</strong> Planning → Delivery
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Emphasize system-wide governance and efficient execution to maintain performance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Role × Scope Matrix Deep Dive */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Role × Scope Matrix Deep Dive</h2>
              <p className="text-sm text-charcoal-600">Understanding organizational responsibility and accountability</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Framework Overview */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Framework Purpose</h3>
                    <p className="text-sm text-charcoal-700 mb-4">
                      The Role × Scope Matrix provides a diagnostic tool for understanding how responsibility 
                      and accountability are distributed across organizational levels and contexts.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Key Applications</h3>
                    <ul className="space-y-2 text-sm text-charcoal-700">
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Organizational design and structure</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Responsibility mapping and clarity</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Decision-making authority and accountability</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Cross-functional coordination and alignment</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Column - Matrix Characteristics */}
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Matrix Characteristics</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        combination: 'Individual × Individual', 
                        characteristics: ['Personal accountability', 'Direct responsibility', 'Clear ownership'],
                        focus: 'Self-management'
                      },
                      { 
                        combination: 'Team × Team', 
                        characteristics: ['Group coordination', 'Shared responsibility', 'Collaborative decision-making'],
                        focus: 'Team effectiveness'
                      },
                      { 
                        combination: 'Organization × Organization', 
                        characteristics: ['System-wide governance', 'Strategic alignment', 'Cross-functional coordination'],
                        focus: 'Organizational coherence'
                      }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-[var(--surface)] p-3 border border-[var(--rule)]">
                        <h4 className="font-semibold text-charcoal-900 mb-1">{item.combination}</h4>
                        <p className="text-xs text-charcoal-600 mb-2">{item.characteristics.join(' • ')}</p>
                        <p className="text-xs font-medium text-consulting-slate-blue-700">Focus: {item.focus}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Frameworks */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-sage-50 to-emerald-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Related Frameworks</h2>
              <p className="text-sm text-charcoal-600">Frameworks that work well with Role × Scope Matrix</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-sage-50 border-2 border-sage-200 p-4">
                  <h3 className="font-semibold text-sage-900 mb-2">Scope Levels</h3>
                  <p className="text-sm text-sage-700 mb-2">Individual, Team, Organization</p>
                  <p className="text-xs text-sage-600">Core scope dimensions for the matrix</p>
                </div>
                <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                  <h3 className="font-semibold text-consulting-slate-blue-900 mb-2">Role Types</h3>
                  <p className="text-sm text-consulting-slate-blue-700 mb-2">Individual Contributor, Team Lead, Executive</p>
                  <p className="text-xs text-consulting-slate-blue-600">Role dimensions that cross-map with scope</p>
                </div>
                <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                  <h3 className="font-semibold text-[var(--mustard)] mb-2">STARS Context</h3>
                  <p className="text-sm text-[var(--mustard)] mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                  <p className="text-xs text-[var(--mustard)]">Context affects optimal role-scope combinations</p>
                </div>
                <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-4">
                  <h3 className="font-semibold text-[var(--text-sec)] mb-2">Capability Dimensions</h3>
                  <p className="text-sm text-[var(--text-sec)] mb-2">12 capability dimensions</p>
                  <p className="text-xs text-[var(--text-sec)]">Capabilities required for effective role-scope alignment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Capability Dimensions (12) Detail Page */}
      {entry.encodedId === 'pf.framework.diagnostic.capabilities.12' && (
        <div className="space-y-6">
          {/* Bounded Attributes Showcase */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-orange-50 to-red-50 px-6 py-4 border-b border-[var(--rule)]">
              <div className="flex items-center gap-3">
                <Target className="h-6 w-6 text-[var(--mustard)]" />
                <div>
                  <h2 className="text-xl font-bold text-charcoal-900">Bounded Attributes in Action</h2>
                  <p className="text-sm text-charcoal-600">How Capability Dimensions (12) connects to the broader system</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Primary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-[var(--mustard-dim)] text-[var(--mustard)] px-2 py-1 rounded text-xs font-bold">PRIMARY</span>
                  Scope Levels
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { scope: 'Individual', description: 'Personal capability development', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                    { scope: 'Team', description: 'Group capability coordination', color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' },
                    { scope: 'Organization', description: 'System-wide capability architecture', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' }
                  ].map((item, idx) => (
                    <div key={idx} className={`p-4  border-2 ${item.color}`}>
                      <h4 className="font-semibold mb-2">{item.scope}</h4>
                      <p className="text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-sage-100 text-sage-800 px-2 py-1 rounded text-xs font-bold">CONNECTORS</span>
                  Related Attributes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-semibold text-sage-900 mb-2">Phase</h4>
                    <p className="text-sm text-sage-700 mb-2">Decide, Engage, Gather, Arrange, Prioritize</p>
                    <p className="text-xs text-sage-600">Different phases require different capability combinations</p>
                  </div>
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-semibold text-consulting-slate-blue-900 mb-2">STARS Mode</h4>
                    <p className="text-sm text-consulting-slate-blue-700 mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                    <p className="text-xs text-consulting-slate-blue-600">Context affects which capabilities to emphasize</p>
                  </div>
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-semibold text-[var(--mustard)] mb-2">Resolution Level</h4>
                    <p className="text-sm text-[var(--mustard)] mb-2">Phrase, Sentence, Paragraph, Page</p>
                    <p className="text-xs text-[var(--mustard)]">Granularity affects capability documentation</p>
                  </div>
                </div>
              </div>

              {/* Practical Usage */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 border-2 border-[var(--mustard)]">
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">How This Works in Practice</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--mustard)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Team-level "Starting New" Project</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Team-level | <strong>Context:</strong> Starting New | <strong>Phase:</strong> Decide → Engage
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Focus on Situational Awareness, Clarity of Focus, and Stakeholder Intelligence to establish direction.
                    </p>
                  </div>
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--mustard)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Organization-level "Sustaining" Operations</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Organization-level | <strong>Context:</strong> Sustaining | <strong>Phase:</strong> Arrange → Prioritize
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Emphasize Process Discipline, Cultural Reinforcement, and Outcome Reflection to maintain performance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Capability Dimensions Deep Dive */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Capability Dimensions (12) Deep Dive</h2>
              <p className="text-sm text-charcoal-600">Understanding organizational capability architecture</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Framework Overview */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Framework Purpose</h3>
                    <p className="text-sm text-charcoal-700 mb-4">
                      The 12 Capability Dimensions provide a comprehensive framework for understanding and developing 
                      organizational capabilities across all levels and contexts.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Key Applications</h3>
                    <ul className="space-y-2 text-sm text-charcoal-700">
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Capability assessment and development</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Organizational design and structure</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Performance improvement and optimization</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Cross-functional alignment and coordination</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Column - Capability Categories */}
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Capability Categories</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        category: 'Contextual Awareness', 
                        capabilities: ['Situational Awareness', 'Stakeholder Intelligence'],
                        focus: 'Understanding context'
                      },
                      { 
                        category: 'Execution Rhythms', 
                        capabilities: ['Rhythm & Ritual', 'Process Discipline'],
                        focus: 'Consistent execution'
                      },
                      { 
                        category: 'Relational Navigation', 
                        capabilities: ['Clarity of Focus', 'Cultural Reinforcement'],
                        focus: 'Relationship management'
                      },
                      { 
                        category: 'Motivational Activation', 
                        capabilities: ['Avoidance & Activation', 'Outcome Reflection'],
                        focus: 'Driving action'
                      }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-[var(--surface)] p-3 border border-[var(--rule)]">
                        <h4 className="font-semibold text-charcoal-900 mb-1">{item.category}</h4>
                        <p className="text-xs text-charcoal-600 mb-2">{item.capabilities.join(' • ')}</p>
                        <p className="text-xs font-medium text-consulting-slate-blue-700">Focus: {item.focus}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Frameworks */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-sage-50 to-emerald-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Related Frameworks</h2>
              <p className="text-sm text-charcoal-600">Frameworks that work well with Capability Dimensions (12)</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-sage-50 border-2 border-sage-200 p-4">
                  <h3 className="font-semibold text-sage-900 mb-2">Capability Families (4)</h3>
                  <p className="text-sm text-sage-700 mb-2">Contextual Awareness, Execution Rhythms, Relational Navigation, Motivational Activation</p>
                  <p className="text-xs text-sage-600">Higher-level groupings of the 12 dimensions</p>
                </div>
                <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                  <h3 className="font-semibold text-consulting-slate-blue-900 mb-2">Scope Levels</h3>
                  <p className="text-sm text-consulting-slate-blue-700 mb-2">Individual, Team, Organization</p>
                  <p className="text-xs text-consulting-slate-blue-600">Capabilities apply across all organizational levels</p>
                </div>
                <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                  <h3 className="font-semibold text-[var(--mustard)] mb-2">STARS Context</h3>
                  <p className="text-sm text-[var(--mustard)] mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                  <p className="text-xs text-[var(--mustard)]">Context affects which capabilities to emphasize</p>
                </div>
                <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-4">
                  <h3 className="font-semibold text-[var(--text-sec)] mb-2">Role × Scope Matrix</h3>
                  <p className="text-sm text-[var(--text-sec)] mb-2">Cross-mapping of roles and scopes</p>
                  <p className="text-xs text-[var(--text-sec)]">Capabilities required for effective role-scope alignment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Capability Families (4) Detail Page */}
      {entry.encodedId === 'pf.framework.diagnostic.capabilities.families' && (
        <div className="space-y-6">
          {/* Bounded Attributes Showcase */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 px-6 py-4 border-b border-[var(--rule)]">
              <div className="flex items-center gap-3">
                <Target className="h-6 w-6 text-[var(--green)]" />
                <div>
                  <h2 className="text-xl font-bold text-charcoal-900">Bounded Attributes in Action</h2>
                  <p className="text-sm text-charcoal-600">How Capability Families (4) connects to the broader system</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Primary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] px-2 py-1 rounded text-xs font-bold">PRIMARY</span>
                  Scope Levels
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { scope: 'Individual', description: 'Personal capability family development', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                    { scope: 'Team', description: 'Group capability family coordination', color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' },
                    { scope: 'Organization', description: 'System-wide capability family architecture', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' }
                  ].map((item, idx) => (
                    <div key={idx} className={`p-4  border-2 ${item.color}`}>
                      <h4 className="font-semibold mb-2">{item.scope}</h4>
                      <p className="text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-sage-100 text-sage-800 px-2 py-1 rounded text-xs font-bold">CONNECTORS</span>
                  Related Attributes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-semibold text-sage-900 mb-2">Phase</h4>
                    <p className="text-sm text-sage-700 mb-2">Decide, Engage, Gather, Arrange, Prioritize</p>
                    <p className="text-xs text-sage-600">Different phases require different capability family combinations</p>
                  </div>
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-semibold text-consulting-slate-blue-900 mb-2">STARS Mode</h4>
                    <p className="text-sm text-consulting-slate-blue-700 mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                    <p className="text-xs text-consulting-slate-blue-600">Context affects which capability families to emphasize</p>
                  </div>
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-semibold text-[var(--mustard)] mb-2">Resolution Level</h4>
                    <p className="text-sm text-[var(--mustard)] mb-2">Phrase, Sentence, Paragraph, Page</p>
                    <p className="text-xs text-[var(--mustard)]">Granularity affects capability family documentation</p>
                  </div>
                </div>
              </div>

              {/* Practical Usage */}
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 border-2 border-[var(--green)]">
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">How This Works in Practice</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--green)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Team-level "Starting New" Project</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Team-level | <strong>Context:</strong> Starting New | <strong>Phase:</strong> Decide → Engage
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Focus on Contextual Awareness and Motivational Activation to establish direction and drive action.
                    </p>
                  </div>
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--green)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Organization-level "Sustaining" Operations</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Organization-level | <strong>Context:</strong> Sustaining | <strong>Phase:</strong> Arrange → Prioritize
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Emphasize Execution Rhythms and Relational Navigation to maintain performance and relationships.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Capability Families Deep Dive */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Capability Families (4) Deep Dive</h2>
              <p className="text-sm text-charcoal-600">Understanding organizational capability architecture</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Framework Overview */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Framework Purpose</h3>
                    <p className="text-sm text-charcoal-700 mb-4">
                      The 4 Capability Families provide a high-level framework for organizing and understanding 
                      organizational capabilities across all levels and contexts.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Key Applications</h3>
                    <ul className="space-y-2 text-sm text-charcoal-700">
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Capability family assessment and development</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Organizational design and structure</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Performance improvement and optimization</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Cross-functional alignment and coordination</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Column - Capability Family Characteristics */}
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Capability Family Characteristics</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        family: 'Contextual Awareness', 
                        characteristics: ['Situational Awareness', 'Stakeholder Intelligence'],
                        focus: 'Understanding context'
                      },
                      { 
                        family: 'Execution Rhythms', 
                        characteristics: ['Rhythm & Ritual', 'Process Discipline'],
                        focus: 'Consistent execution'
                      },
                      { 
                        family: 'Relational Navigation', 
                        characteristics: ['Clarity of Focus', 'Cultural Reinforcement'],
                        focus: 'Relationship management'
                      },
                      { 
                        family: 'Motivational Activation', 
                        characteristics: ['Avoidance & Activation', 'Outcome Reflection'],
                        focus: 'Driving action'
                      }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-[var(--surface)] p-3 border border-[var(--rule)]">
                        <h4 className="font-semibold text-charcoal-900 mb-1">{item.family}</h4>
                        <p className="text-xs text-charcoal-600 mb-2">{item.characteristics.join(' • ')}</p>
                        <p className="text-xs font-medium text-consulting-slate-blue-700">Focus: {item.focus}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Frameworks */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-sage-50 to-emerald-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Related Frameworks</h2>
              <p className="text-sm text-charcoal-600">Frameworks that work well with Capability Families (4)</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-sage-50 border-2 border-sage-200 p-4">
                  <h3 className="font-semibold text-sage-900 mb-2">Capability Dimensions (12)</h3>
                  <p className="text-sm text-sage-700 mb-2">12 detailed capability dimensions</p>
                  <p className="text-xs text-sage-600">Detailed breakdown of the 4 capability families</p>
                </div>
                <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                  <h3 className="font-semibold text-consulting-slate-blue-900 mb-2">Scope Levels</h3>
                  <p className="text-sm text-consulting-slate-blue-700 mb-2">Individual, Team, Organization</p>
                  <p className="text-xs text-consulting-slate-blue-600">Capability families apply across all organizational levels</p>
                </div>
                <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                  <h3 className="font-semibold text-[var(--mustard)] mb-2">STARS Context</h3>
                  <p className="text-sm text-[var(--mustard)] mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                  <p className="text-xs text-[var(--mustard)]">Context affects which capability families to emphasize</p>
                </div>
                <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-4">
                  <h3 className="font-semibold text-[var(--text-sec)] mb-2">Role × Scope Matrix</h3>
                  <p className="text-sm text-[var(--text-sec)] mb-2">Cross-mapping of roles and scopes</p>
                  <p className="text-xs text-[var(--text-sec)]">Capability families required for effective role-scope alignment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scope Levels Detail Page */}
      {entry.encodedId === 'pf.framework.diagnostic.scope.levels' && (
        <div className="space-y-6">
          {/* Bounded Attributes Showcase */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-6 py-4 border-b border-[var(--rule)]">
              <div className="flex items-center gap-3">
                <Target className="h-6 w-6 text-[var(--text-ter)]" />
                <div>
                  <h2 className="text-xl font-bold text-charcoal-900">Bounded Attributes in Action</h2>
                  <p className="text-sm text-charcoal-600">How Scope Levels connects to the broader system</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Primary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-[var(--slate-dim)] text-[var(--text-sec)] px-2 py-1 rounded text-xs font-bold">PRIMARY</span>
                  Scope Levels
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { scope: 'Individual', description: 'Personal responsibility and accountability', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                    { scope: 'Team', description: 'Group coordination and collaboration', color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' },
                    { scope: 'Organization', description: 'System-wide strategy and governance', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' }
                  ].map((item, idx) => (
                    <div key={idx} className={`p-4  border-2 ${item.color}`}>
                      <h4 className="font-semibold mb-2">{item.scope}</h4>
                      <p className="text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Bounded Attributes */}
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                  <span className="bg-sage-100 text-sage-800 px-2 py-1 rounded text-xs font-bold">CONNECTORS</span>
                  Related Attributes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-semibold text-sage-900 mb-2">Phase</h4>
                    <p className="text-sm text-sage-700 mb-2">Strategy, Prioritization, Planning, Delivery, Decide, Engage, Gather, Arrange, Prioritize, Intake, Item Work, Ranking, Execution</p>
                    <p className="text-xs text-sage-600">Different phases operate at different scope levels</p>
                  </div>
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-semibold text-consulting-slate-blue-900 mb-2">STARS Mode</h4>
                    <p className="text-sm text-consulting-slate-blue-700 mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                    <p className="text-xs text-consulting-slate-blue-600">Context affects which scope levels to emphasize</p>
                  </div>
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-semibold text-[var(--mustard)] mb-2">Resolution Level</h4>
                    <p className="text-sm text-[var(--mustard)] mb-2">Phrase, Sentence, Paragraph, Page</p>
                    <p className="text-xs text-[var(--mustard)]">Granularity affects scope level documentation</p>
                  </div>
                </div>
              </div>

              {/* Practical Usage */}
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 p-6 border-2 border-[var(--slate)]">
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">How This Works in Practice</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--slate)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Individual-level "Starting New" Project</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Individual-level | <strong>Context:</strong> Starting New | <strong>Phase:</strong> Strategy → Prioritization
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Focus on personal goal setting and individual prioritization to establish direction.
                    </p>
                  </div>
                  <div className="bg-[var(--bg-raised)] p-4 border border-[var(--slate)]">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Example: Organization-level "Sustaining" Operations</h4>
                    <p className="text-sm text-charcoal-700 mb-2">
                      <strong>Scope:</strong> Organization-level | <strong>Context:</strong> Sustaining | <strong>Phase:</strong> Planning → Delivery
                    </p>
                    <p className="text-sm text-charcoal-600">
                      Emphasize system-wide governance and efficient execution to maintain performance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scope Levels Deep Dive */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Scope Levels Deep Dive</h2>
              <p className="text-sm text-charcoal-600">Understanding organizational scope and responsibility</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Framework Overview */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Framework Purpose</h3>
                    <p className="text-sm text-charcoal-700 mb-4">
                      The 3 Scope Levels provide a fundamental framework for understanding how responsibility 
                      and accountability are distributed across organizational levels.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Key Applications</h3>
                    <ul className="space-y-2 text-sm text-charcoal-700">
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Organizational design and structure</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Responsibility mapping and clarity</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Decision-making authority and accountability</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-consulting-slate-blue-600 mt-1">•</span>
                        <span>Cross-functional coordination and alignment</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Column - Scope Level Characteristics */}
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Scope Level Characteristics</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        level: 'Individual', 
                        characteristics: ['Personal accountability', 'Direct responsibility', 'Clear ownership'],
                        focus: 'Self-management'
                      },
                      { 
                        level: 'Team', 
                        characteristics: ['Group coordination', 'Shared responsibility', 'Collaborative decision-making'],
                        focus: 'Team effectiveness'
                      },
                      { 
                        level: 'Organization', 
                        characteristics: ['System-wide governance', 'Strategic alignment', 'Cross-functional coordination'],
                        focus: 'Organizational coherence'
                      }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-[var(--surface)] p-3 border border-[var(--rule)]">
                        <h4 className="font-semibold text-charcoal-900 mb-1">{item.level}</h4>
                        <p className="text-xs text-charcoal-600 mb-2">{item.characteristics.join(' • ')}</p>
                        <p className="text-xs font-medium text-consulting-slate-blue-700">Focus: {item.focus}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Frameworks */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-sage-50 to-emerald-50 px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-xl font-bold text-charcoal-900">Related Frameworks</h2>
              <p className="text-sm text-charcoal-600">Frameworks that work well with Scope Levels</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-sage-50 border-2 border-sage-200 p-4">
                  <h3 className="font-semibold text-sage-900 mb-2">Role × Scope Matrix</h3>
                  <p className="text-sm text-sage-700 mb-2">Cross-mapping of roles and scopes</p>
                  <p className="text-xs text-sage-600">Core scope dimensions for the matrix</p>
                </div>
                <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                  <h3 className="font-semibold text-consulting-slate-blue-900 mb-2">Role Types</h3>
                  <p className="text-sm text-consulting-slate-blue-700 mb-2">Individual Contributor, Team Lead, Executive</p>
                  <p className="text-xs text-consulting-slate-blue-600">Role dimensions that cross-map with scope</p>
                </div>
                <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                  <h3 className="font-semibold text-[var(--mustard)] mb-2">STARS Context</h3>
                  <p className="text-sm text-[var(--mustard)] mb-2">Starting New, Ending, Turnaround, Scaling, Sustaining</p>
                  <p className="text-xs text-[var(--mustard)]">Context affects optimal scope level combinations</p>
                </div>
                <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-4">
                  <h3 className="font-semibold text-[var(--text-sec)] mb-2">Capability Dimensions</h3>
                  <p className="text-sm text-[var(--text-sec)] mb-2">12 capability dimensions</p>
                  <p className="text-xs text-[var(--text-sec)]">Capabilities required for effective scope level alignment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full M3 Encoding */}
      {fullEncoding && (
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
            <div className="bg-gradient-to-r from-sage-50 to-consulting-slate-blue-50 px-6 py-4 border-b border-[var(--rule)]">
            <div className="flex items-center gap-3">
              <Layers className="h-6 w-6 text-sage-600" />
              <div>
                <h2 className="text-xl font-bold text-charcoal-900">M3 Grammar Encoding</h2>
                <p className="text-sm text-charcoal-600">Four-Layer Meta-Stack Representation</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {/* Ontology */}
            {fullEncoding.ontology && (
              <div className="border-2 border-consulting-slate-blue-200 overflow-hidden">
                <button
                  onClick={() => toggleSection('ontology')}
                  className="w-full px-4 py-3 bg-consulting-slate-blue-50 flex items-center justify-between hover:bg-consulting-slate-blue-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-consulting-slate-blue-600" />
                    <h3 className="font-bold text-consulting-slate-blue-900 text-lg">Ontology</h3>
                    <span className="text-sm text-consulting-slate-blue-700">What exists</span>
                  </div>
                  {expandedSections.has('ontology') ? (
                    <ChevronDown className="h-5 w-5 text-consulting-slate-blue-600" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-consulting-slate-blue-600" />
                  )}
                </button>
                
                {expandedSections.has('ontology') && (
                  <div className="p-4 space-y-3 bg-[var(--bg-raised)]">
                    {fullEncoding.ontology.category && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-1">Category:</h4>
                        <div className="flex flex-wrap gap-2">
                          {fullEncoding.ontology.category.map((cat, idx) => (
                            <span key={idx} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-sm">
                              {cat}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {fullEncoding.ontology.structures && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-1">Structures:</h4>
                        <div className="flex flex-wrap gap-2">
                          {fullEncoding.ontology.structures.map((struct, idx) => (
                            <span key={idx} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-sm">
                              {struct}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {fullEncoding.ontology.relationships && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-1">Relationships:</h4>
                        {fullEncoding.ontology.relationships.map((rel, idx) => (
                          <div key={idx} className="text-sm text-charcoal-700 pl-4 border-l-2 border-consulting-slate-blue-300 mb-2">
                            <span className="font-medium">{rel.relationshipType}:</span> {rel.description}
                          </div>
                        ))}
                      </div>
                    )}
                    {fullEncoding.ontology.systems && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-1">Systems:</h4>
                        <div className="flex flex-wrap gap-2">
                          {fullEncoding.ontology.systems.map((sys, idx) => (
                            <span key={idx} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-sm">
                              {sys}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {fullEncoding.ontology.entities && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-1">Entities:</h4>
                        <div className="flex flex-wrap gap-2">
                          {fullEncoding.ontology.entities.map((entity, idx) => (
                            <span key={idx} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-sm font-medium">
                              {entity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {fullEncoding.ontology.definitions && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-1">Definitions:</h4>
                        {Object.entries(fullEncoding.ontology.definitions).map(([key, value]) => (
                          <div key={key} className="text-sm text-charcoal-700">
                            <span className="font-medium">{key}:</span> {value}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Modality */}
            {fullEncoding.modality && (
              <div className="border-2 border-sage-200 overflow-hidden">
                <button
                  onClick={() => toggleSection('modality')}
                  className="w-full px-4 py-3 bg-sage-50 flex items-center justify-between hover:bg-sage-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Code className="h-5 w-5 text-sage-600" />
                    <h3 className="font-bold text-sage-900 text-lg">Modality</h3>
                    <span className="text-sm text-sage-700">How properties vary</span>
                  </div>
                  {expandedSections.has('modality') ? (
                    <ChevronDown className="h-5 w-5 text-sage-600" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-sage-600" />
                  )}
                </button>
                
                {expandedSections.has('modality') && (
                  <div className="p-4 space-y-3 bg-[var(--bg-raised)]">
                    {fullEncoding.modality.attributes && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Attributes:</h4>
                        {fullEncoding.modality.attributes.map((attr, idx) => (
                          <div key={idx} className="text-sm text-charcoal-700 pl-4 border-l-2 border-sage-300 mb-2">
                            <span className="font-medium">{attr.attributeType}:</span> {attr.measurementScale}
                          </div>
                        ))}
                      </div>
                    )}
                    {fullEncoding.modality.states && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">States:</h4>
                        {fullEncoding.modality.states.map((state, idx) => (
                          <div key={idx} className="text-sm text-charcoal-700 pl-4 border-l-2 border-sage-300 mb-2">
                            <span className="font-medium">{state.stateType}:</span> {state.stateValues}
                          </div>
                        ))}
                      </div>
                    )}
                    {fullEncoding.modality.contexts && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-1">Contexts:</h4>
                        <div className="flex flex-wrap gap-2">
                          {fullEncoding.modality.contexts.map((ctx, idx) => (
                            <span key={idx} className="px-2 py-1 bg-sage-100 text-sage-800 rounded text-sm">
                              {ctx}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {fullEncoding.modality.constraints && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Constraints:</h4>
                        {fullEncoding.modality.constraints.map((constraint, idx) => (
                          <div key={idx} className="text-sm text-charcoal-700 pl-4 border-l-2 border-sage-300 mb-2">
                            <span className="font-medium">{constraint.constraintType}:</span> {constraint.description}
                          </div>
                        ))}
                      </div>
                    )}
                    {fullEncoding.modality.identity && (
                      <div className="bg-sage-50 rounded p-3">
                        <h4 className="font-semibold text-charcoal-900 mb-2">Identity & Persistence:</h4>
                        <div className="space-y-1 text-sm">
                          <div><span className="font-medium">Stable ID:</span> {fullEncoding.modality.identity.stableId}</div>
                          <div><span className="font-medium">Version:</span> {fullEncoding.modality.identity.versioning}</div>
                          <div><span className="font-medium">Lineage:</span> {fullEncoding.modality.identity.lineage.join(', ')}</div>
                          <div><span className="font-medium">Invariants:</span> {fullEncoding.modality.identity.invariants}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Dynamics */}
            {fullEncoding.dynamics && (
              <div className="border-2 border-[var(--mustard)] overflow-hidden">
                <button
                  onClick={() => toggleSection('dynamics')}
                  className="w-full px-4 py-3 bg-[var(--mustard-dim)] flex items-center justify-between hover:bg-[var(--mustard-dim)] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <ChevronRight className="h-5 w-5 text-[var(--mustard)]" />
                    <h3 className="font-bold text-[var(--mustard)] text-lg">Dynamics</h3>
                    <span className="text-sm text-[var(--mustard)]">How things change</span>
                  </div>
                  {expandedSections.has('dynamics') ? (
                    <ChevronDown className="h-5 w-5 text-[var(--mustard)]" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-[var(--mustard)]" />
                  )}
                </button>
                
                {expandedSections.has('dynamics') && (
                  <div className="p-4 space-y-3 bg-[var(--bg-raised)]">
                    {fullEncoding.dynamics.rules && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Rules:</h4>
                        {fullEncoding.dynamics.rules.map((rule, idx) => (
                          <div key={idx} className="bg-[var(--mustard-dim)] rounded p-3 mb-2">
                            <div className="font-medium text-[var(--mustard)]">{rule.ruleType}</div>
                            <div className="text-sm text-charcoal-700">{rule.ruleDescription}</div>
                            {rule.sourceNotes && (
                              <div className="text-xs text-charcoal-600 mt-1 italic">{rule.sourceNotes}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {fullEncoding.dynamics.processes && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Processes:</h4>
                        {fullEncoding.dynamics.processes.map((proc, idx) => (
                          <div key={idx} className="text-sm text-charcoal-700 pl-4 border-l-2 border-[var(--mustard)] mb-2">
                            <span className="font-medium">{proc.processType}:</span> {proc.processDescription}
                          </div>
                        ))}
                      </div>
                    )}
                    {fullEncoding.dynamics.timing && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Timing:</h4>
                        <div className="bg-[var(--mustard-dim)] rounded p-3 space-y-1 text-sm">
                          {Object.entries(fullEncoding.dynamics.timing).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium">{key}:</span> {value}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {fullEncoding.dynamics.causality && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Causality:</h4>
                        {fullEncoding.dynamics.causality.map((causal, idx) => (
                          <div key={idx} className="text-sm text-charcoal-700 pl-4 border-l-2 border-[var(--mustard)] mb-2">
                            <span className="font-medium">{causal.causalType}:</span> {causal.causalDescription}
                          </div>
                        ))}
                      </div>
                    )}
                    {fullEncoding.dynamics.uncertainty && (
                      <div className="bg-[var(--mustard-dim)] rounded p-3">
                        <h4 className="font-semibold text-charcoal-900 mb-2">Uncertainty:</h4>
                        <div className="space-y-1 text-sm">
                          {fullEncoding.dynamics.uncertainty.confidenceLevels && (
                            <div><span className="font-medium">Confidence:</span> {fullEncoding.dynamics.uncertainty.confidenceLevels}</div>
                          )}
                          {fullEncoding.dynamics.uncertainty.sensitivity && (
                            <div><span className="font-medium">Sensitivity:</span> {fullEncoding.dynamics.uncertainty.sensitivity}</div>
                          )}
                          {fullEncoding.dynamics.uncertainty.volatilityIndicators && (
                            <div><span className="font-medium">Volatility:</span> {fullEncoding.dynamics.uncertainty.volatilityIndicators}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Evaluation */}
            {fullEncoding.evaluation && (
              <div className="border-2 border-[var(--mustard)] overflow-hidden">
                <button
                  onClick={() => toggleSection('evaluation')}
                  className="w-full px-4 py-3 bg-[var(--mustard-dim)] flex items-center justify-between hover:bg-[var(--mustard-dim)] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-[var(--mustard)]" />
                    <h3 className="font-bold text-[var(--mustard)] text-lg">Evaluation</h3>
                    <span className="text-sm text-[var(--mustard)]">Why it matters</span>
                  </div>
                  {expandedSections.has('evaluation') ? (
                    <ChevronDown className="h-5 w-5 text-[var(--mustard)]" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-[var(--mustard)]" />
                  )}
                </button>
                
                {expandedSections.has('evaluation') && (
                  <div className="p-4 space-y-3 bg-[var(--bg-raised)]">
                    {fullEncoding.evaluation.intent && (
                      <div className="bg-[var(--mustard-dim)] rounded p-3">
                        <h4 className="font-semibold text-charcoal-900 mb-2">Intent:</h4>
                        <div className="grid md:grid-cols-2 gap-2 text-sm">
                          {fullEncoding.evaluation.intent.actorScope && (
                            <div><span className="font-medium">Actor Scope:</span> {fullEncoding.evaluation.intent.actorScope.join(', ')}</div>
                          )}
                          {fullEncoding.evaluation.intent.stakeholders && (
                            <div><span className="font-medium">Stakeholders:</span> {fullEncoding.evaluation.intent.stakeholders.join(', ')}</div>
                          )}
                          {fullEncoding.evaluation.intent.timeHorizon && (
                            <div><span className="font-medium">Time Horizon:</span> {fullEncoding.evaluation.intent.timeHorizon.join(', ')}</div>
                          )}
                          {fullEncoding.evaluation.intent.motivationalFrame && (
                            <div><span className="font-medium">Motivational Frame:</span> {fullEncoding.evaluation.intent.motivationalFrame.join(', ')}</div>
                          )}
                          {fullEncoding.evaluation.intent.ethicalAnchors && (
                            <div><span className="font-medium">Ethical Anchors:</span> {fullEncoding.evaluation.intent.ethicalAnchors.join(', ')}</div>
                          )}
                          {fullEncoding.evaluation.intent.priorityAims && (
                            <div className="md:col-span-2"><span className="font-medium">Priority Aims:</span> {fullEncoding.evaluation.intent.priorityAims.join(', ')}</div>
                          )}
                        </div>
                      </div>
                    )}
                    {fullEncoding.evaluation.utility && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Utility:</h4>
                        {fullEncoding.evaluation.utility.utilityDimensions && (
                          <div className="mb-2">
                            <span className="text-sm font-medium">Dimensions:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {fullEncoding.evaluation.utility.utilityDimensions.map((dim, idx) => (
                                <span key={idx} className="px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-sm">
                                  {dim}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {fullEncoding.evaluation.utility.tradeoffPolicies && (
                          <div className="text-sm text-charcoal-700">
                            <span className="font-medium">Tradeoff Policies:</span> {fullEncoding.evaluation.utility.tradeoffPolicies.join('; ')}
                          </div>
                        )}
                      </div>
                    )}
                    {fullEncoding.evaluation.observation && (
                      <div>
                        <h4 className="font-semibold text-charcoal-900 mb-2">Observation & Measurement:</h4>
                        {fullEncoding.evaluation.observation.metrics && (
                          <div className="mb-2">
                            <span className="text-sm font-medium">Metrics:</span>
                            {fullEncoding.evaluation.observation.metrics.map((metric, idx) => (
                              <div key={idx} className="text-sm text-charcoal-700 pl-4 border-l-2 border-[var(--mustard)] mb-1 mt-1">
                                <span className="font-medium">{metric.metricType}:</span> {metric.metricDescription}
                              </div>
                            ))}
                          </div>
                        )}
                        {fullEncoding.evaluation.observation.signals && (
                          <div className="text-sm mb-2">
                            <span className="font-medium">Signals:</span> {fullEncoding.evaluation.observation.signals.join(', ')}
                          </div>
                        )}
                        {fullEncoding.evaluation.observation.indicatorClasses && (
                          <div className="text-sm mb-2">
                            <span className="font-medium">Indicator Classes:</span> {fullEncoding.evaluation.observation.indicatorClasses.join(', ')}
                          </div>
                        )}
                        {fullEncoding.evaluation.observation.resolutionCadence && (
                          <div className="text-sm">
                            <span className="font-medium">Resolution Cadence:</span> {fullEncoding.evaluation.observation.resolutionCadence}
                          </div>
                        )}
                      </div>
                    )}
                    {fullEncoding.evaluation.adaptation && (
                      <div className="bg-[var(--mustard-dim)] rounded p-3">
                        <h4 className="font-semibold text-charcoal-900 mb-2">Adaptation:</h4>
                        <div className="space-y-1 text-sm">
                          {fullEncoding.evaluation.adaptation.feedbackLoops && (
                            <div><span className="font-medium">Feedback Loops:</span> {fullEncoding.evaluation.adaptation.feedbackLoops.join(', ')}</div>
                          )}
                          {fullEncoding.evaluation.adaptation.scopeOfChange && (
                            <div><span className="font-medium">Scope of Change:</span> {fullEncoding.evaluation.adaptation.scopeOfChange.join(', ')}</div>
                          )}
                          {fullEncoding.evaluation.adaptation.learningDepth && (
                            <div><span className="font-medium">Learning Depth:</span> {fullEncoding.evaluation.adaptation.learningDepth.join(', ')}</div>
                          )}
                          {fullEncoding.evaluation.adaptation.memoryTraceability && (
                            <div>
                              <span className="font-medium">Memory Traceability:</span>
                              <div className="mt-1 flex flex-wrap gap-2">
                                {Object.entries(fullEncoding.evaluation.adaptation.memoryTraceability).map(([key, value]) => (
                                  <span key={key} className={`px-2 py-1 rounded text-xs ${value ? 'bg-sage-100 text-sage-800' : 'bg-[var(--hover-tint)] text-charcoal-600'}`}>
                                    {key}: {value ? '✓' : '✗'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* JSON Representation */}
            <div className="border-t-2 border-[var(--rule)] mt-6 pt-6">
              <button
                onClick={() => toggleSection('json-representation')}
                className="w-full flex items-center justify-between px-4 py-3 bg-charcoal-50 hover:bg-charcoal-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Code className="h-5 w-5 text-charcoal-600" />
                  <h3 className="font-bold text-charcoal-900 text-lg">JSON Representation</h3>
                  <span className="text-sm text-charcoal-600">Machine-readable format</span>
                </div>
                {expandedSections.has('json-representation') ? (
                  <ChevronDown className="h-5 w-5 text-charcoal-600" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-charcoal-600" />
                )}
              </button>
              
              {expandedSections.has('json-representation') && (
                <div className="mt-4">
                  <div className="bg-charcoal-900 p-4 overflow-x-auto">
                    <pre className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-mono">
                      {JSON.stringify(fullEncoding, null, 2)}
                    </pre>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(fullEncoding, null, 2));
                      }}
                      className="px-3 py-1.5 bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 text-white text-sm rounded transition-colors"
                    >
                      Copy JSON
                    </button>
                    <button
                      onClick={() => {
                        const blob = new Blob([JSON.stringify(fullEncoding, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${entry.encodedId}-m3-encoding.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="px-3 py-1.5 bg-sage-600 hover:bg-sage-700 text-white text-sm rounded transition-colors"
                    >
                      Download JSON
                    </button>
                  </div>
                  <p className="text-xs text-charcoal-600 mt-2">
                    This JSON representation can be used for data exchange, API integration, or programmatic access to the M3 encoding.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* No M3 Encoding Notice */}
      {!fullEncoding && (
        <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-6">
          <div className="flex items-start gap-3">
            <Database className="h-6 w-6 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-[var(--mustard)] mb-1">No M3 Encoding Available</h3>
              <p className="text-[var(--mustard)] text-sm">
                This registry entry has not yet been encoded using the full Meta-Stack grammar. 
                Only basic registry information is available.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Next Steps - Cross-Feature CTAs */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 via-sage-50 to-amber-50 p-6 border-2 border-consulting-slate-blue-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
          <TrendingUp className="h-6 w-6 text-consulting-slate-blue-600" />
          What's Next?
        </h2>
        <p className="text-charcoal-700 mb-6">
          Now that you've explored <strong>{entry.canonicalName}</strong>, here are some ways to deepen your understanding and apply what you've learned:
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Try in Workshop */}
          <Link
            to="/elevation/workshops"
            className="flex items-start gap-4 p-4 bg-[var(--bg-raised)] border-2 border-sage-200 hover:border-sage-400 hover:shadow-md transition-all group"
          >
            <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Rocket className="h-6 w-6 text-sage-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-charcoal-900 mb-1 group-hover:text-sage-700 transition-colors">
                Practice in Workshop
              </h3>
              <p className="text-sm text-charcoal-600">
                Try interactive simulations to learn by doing
              </p>
            </div>
          </Link>

          {/* Track an Outcome */}
          <Link
            to="/elevation/outcomes"
            className="flex items-start gap-4 p-4 bg-[var(--bg-raised)] border-2 border-[var(--mustard)] hover:border-[var(--mustard)] hover:shadow-md transition-all group"
          >
            <div className="w-12 h-12 rounded-full bg-[var(--mustard-dim)] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Target className="h-6 w-6 text-[var(--mustard)]" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-charcoal-900 mb-1 group-hover:text-[var(--mustard)] transition-colors">
                Apply to an Outcome
              </h3>
              <p className="text-sm text-charcoal-600">
                Track how this framework helps achieve real goals
              </p>
            </div>
          </Link>

          {/* See Team Stories */}
          <Link
            to="/elevation/team"
            className="flex items-start gap-4 p-4 bg-[var(--bg-raised)] border-2 border-coral-200 hover:border-coral-400 hover:shadow-md transition-all group"
          >
            <div className="w-12 h-12 rounded-full bg-coral-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Users className="h-6 w-6 text-coral-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-charcoal-900 mb-1 group-hover:text-coral-700 transition-colors">
                See How Others Use This
              </h3>
              <p className="text-sm text-charcoal-600">
                Read real-world success stories from your team
              </p>
            </div>
          </Link>

          {/* Explore Related */}
          <Link
            to="/elevation/advanced-search"
            className="flex items-start gap-4 p-4 bg-[var(--bg-raised)] border-2 border-consulting-slate-blue-200 hover:border-consulting-slate-blue-400 hover:shadow-md transition-all group"
          >
            <div className="w-12 h-12 rounded-full bg-consulting-slate-blue-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <BookOpen className="h-6 w-6 text-consulting-slate-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-charcoal-900 mb-1 group-hover:text-consulting-slate-blue-700 transition-colors">
                Find Related Frameworks
              </h3>
              <p className="text-sm text-charcoal-600">
                Discover similar or complementary models
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};